/**
 * Agent Identity — Profile management, NPN-based identity, localStorage cache
 */

const API_BASE = '';
const STORAGE_KEY = 'wq_agent_npn';
const PROFILE_CACHE_KEY = 'wq_agent_profile';

/**
 * Get the current agent's NPN from localStorage
 * @returns {string|null}
 */
export function getAgentNpn() {
  return localStorage.getItem(STORAGE_KEY);
}

/**
 * Check if an agent is registered (has NPN in localStorage)
 * @returns {boolean}
 */
export function isRegistered() {
  return !!getAgentNpn();
}

/**
 * Set the agent NPN in localStorage
 * @param {string} npn
 */
export function setAgentNpn(npn) {
  localStorage.setItem(STORAGE_KEY, npn);
}

/**
 * Get cached agent profile from localStorage
 * @returns {Object|null}
 */
export function getCachedProfile() {
  const cached = localStorage.getItem(PROFILE_CACHE_KEY);
  if (!cached) return null;
  try {
    return JSON.parse(cached);
  } catch {
    return null;
  }
}

/**
 * Cache agent profile to localStorage
 * @param {Object} profile
 */
export function cacheProfile(profile) {
  localStorage.setItem(PROFILE_CACHE_KEY, JSON.stringify(profile));
}

/**
 * Register a new agent
 * @param {Object} data - { npn, name, email, phone, title, tagline, licenseStates }
 * @returns {Object} Created agent profile
 */
export async function registerAgent(data) {
  const response = await fetch(`${API_BASE}/agents/register`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Registration failed' }));
    throw new Error(error.error || `Registration failed: ${response.status}`);
  }

  const result = await response.json();
  const profile = result.data;

  setAgentNpn(profile.npn);
  cacheProfile(profile);

  return profile;
}

/**
 * Fetch agent profile from API
 * @param {string} npn
 * @returns {Object} Agent profile
 */
export async function fetchProfile(npn) {
  const response = await fetch(`${API_BASE}/agents/get?npn=${encodeURIComponent(npn)}`);
  if (!response.ok) throw new Error(`Failed to fetch profile: ${response.status}`);
  const result = await response.json();
  cacheProfile(result.data);
  return result.data;
}

/**
 * Update agent profile
 * @param {string} npn
 * @param {Object} updates - Partial profile fields to update
 * @returns {Object} Updated profile
 */
export async function updateProfile(npn, updates) {
  const response = await fetch(`${API_BASE}/agents/update`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ npn, ...updates }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Update failed' }));
    throw new Error(error.error || `Update failed: ${response.status}`);
  }

  const result = await response.json();
  cacheProfile(result.data);
  return result.data;
}

/**
 * Get agent profile — tries cache first, then API with background refresh
 * @returns {Object|null} Agent profile or null if not registered
 */
export async function getProfile() {
  const npn = getAgentNpn();
  if (!npn) return null;

  const cached = getCachedProfile();
  if (cached) {
    // Background refresh
    fetchProfile(npn).catch(() => {});
    return cached;
  }

  return fetchProfile(npn);
}

/**
 * Build agent branding object from profile (for backward compatibility with existing agentBranding)
 * @param {Object} profile
 * @returns {Object} agentBranding-compatible object
 */
export function toBranding(profile) {
  return {
    name: profile.name || 'Your Name',
    title: profile.title || 'Insurance Agent',
    email: profile.email || '',
    phone: profile.phone || '',
    tagline: profile.tagline || '',
    logoUrl: profile.logoUrl || '',
  };
}

export const AgentIdentity = {
  getAgentNpn,
  isRegistered,
  setAgentNpn,
  getCachedProfile,
  registerAgent,
  fetchProfile,
  updateProfile,
  getProfile,
  toBranding,
};
